'use client';

/**
 * Homepage - Ticket to Luxury
 *
 * Dark elegant landing page for VIP concert experiences
 */

import Link from 'next/link';
import { Button } from '@/components/ui/button';
import { FeaturedCarousel } from '@/components/FeaturedCarousel';
import { EventCard } from '@/components/EventCard';
import { TestimonialsSection } from '@/components/TestimonialsSection';
import {
  mockEvents,
  mockTestimonials,
  getFeaturedEvents,
  getUpcomingEvents,
} from '@/lib/mock-data';
import {
  Sparkles,
  Crown,
  Ticket,
  MapPin,
  Clock,
  Shield,
  Star,
  ChevronRight,
} from 'lucide-react';

export default function HomePage() {
  const featuredEvents = getFeaturedEvents();
  const upcomingEvents = getUpcomingEvents().slice(0, 6);

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-20">
        {/* Animated Background */}
        <div className="absolute inset-0 bg-gradient-to-b from-background via-background/95 to-background">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(212,175,55,0.1),transparent_50%)]" />
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_80%_20%,rgba(183,110,121,0.1),transparent_50%)]" />
        </div>

        <div className="container relative z-10 mx-auto px-4 py-20">
          <div className="max-w-5xl mx-auto text-center space-y-8">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 backdrop-blur-sm border border-primary/30 animate-in fade-in slide-in-from-bottom-4 duration-700">
              <Crown className="h-4 w-4 text-primary" />
              <span className="text-sm font-medium text-primary">
                Premier VIP Concert Experiences
              </span>
            </div>

            {/* Main Headline */}
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold leading-tight animate-in fade-in slide-in-from-bottom-6 duration-1000">
              Experience Concerts
              <br />
              <span className="text-gold-gradient">Like Never Before</span>
            </h1>

            {/* Subheadline */}
            <p className="text-lg md:text-xl lg:text-2xl text-muted-foreground max-w-3xl mx-auto animate-in fade-in slide-in-from-bottom-8 duration-1000 delay-200">
              Exclusive VIP access to international artists across Bangladesh, India, Nepal, and Sri Lanka.
              Premium seating, backstage access, and unforgettable luxury experiences.
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 pt-4 animate-in fade-in slide-in-from-bottom-10 duration-1000 delay-300">
              <Button
                size="lg"
                className="bg-gradient-to-r from-primary to-secondary hover:shadow-gold text-lg px-8 py-6"
                asChild
              >
                <Link href="/events">
                  <Ticket className="mr-2 h-5 w-5" />
                  Explore VIP Packages
                </Link>
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="border-primary/50 text-primary hover:bg-primary hover:text-primary-foreground text-lg px-8 py-6"
                asChild
              >
                <Link href="/how-it-works">
                  Learn More
                  <ChevronRight className="ml-2 h-5 w-5" />
                </Link>
              </Button>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 pt-16 animate-in fade-in slide-in-from-bottom-12 duration-1000 delay-500">
              <div className="space-y-2">
                <div className="text-3xl md:text-4xl font-bold text-primary">2,000+</div>
                <div className="text-sm text-muted-foreground">Happy VIP Guests</div>
              </div>
              <div className="space-y-2">
                <div className="text-3xl md:text-4xl font-bold text-primary">50+</div>
                <div className="text-sm text-muted-foreground">Exclusive Events</div>
              </div>
              <div className="space-y-2">
                <div className="text-3xl md:text-4xl font-bold text-primary">4</div>
                <div className="text-sm text-muted-foreground">Countries Served</div>
              </div>
              <div className="space-y-2">
                <div className="text-3xl md:text-4xl font-bold text-primary">5★</div>
                <div className="text-sm text-muted-foreground">Average Rating</div>
              </div>
            </div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 rounded-full border-2 border-primary/50 flex items-start justify-center p-2">
            <div className="w-1 h-3 rounded-full bg-primary animate-pulse" />
          </div>
        </div>
      </section>

      {/* Featured Events Carousel */}
      <section className="py-20 md:py-32">
        <div className="container mx-auto px-4">
          <div className="mb-12 text-center space-y-4">
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold">
              Featured <span className="text-gold-gradient">VIP Events</span>
            </h2>
            <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto">
              Don't miss these exclusive opportunities for unforgettable concert experiences
            </p>
          </div>

          <FeaturedCarousel events={featuredEvents} />
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 md:py-32 bg-gradient-to-b from-transparent via-primary/5 to-transparent">
        <div className="container mx-auto px-4">
          <div className="text-center max-w-3xl mx-auto mb-16 space-y-4">
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold">
              The <span className="text-gold-gradient">Luxury</span> Difference
            </h2>
            <p className="text-lg md:text-xl text-muted-foreground">
              Experience world-class concerts with unparalleled VIP treatment and exclusive perks
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="glass-card p-8 space-y-4 hover-glow">
              <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center">
                <Crown className="h-7 w-7 text-primary" />
              </div>
              <h3 className="text-2xl font-bold">Premium VIP Access</h3>
              <p className="text-muted-foreground">
                Exclusive backstage passes, meet & greets with artists, and front-row seating for an intimate experience.
              </p>
            </div>

            <div className="glass-card p-8 space-y-4 hover-glow">
              <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center">
                <Sparkles className="h-7 w-7 text-primary" />
              </div>
              <h3 className="text-2xl font-bold">Luxury Amenities</h3>
              <p className="text-muted-foreground">
                Gourmet dining, premium beverages, VIP lounges, and personal concierge service throughout the event.
              </p>
            </div>

            <div className="glass-card p-8 space-y-4 hover-glow">
              <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center">
                <MapPin className="h-7 w-7 text-primary" />
              </div>
              <h3 className="text-2xl font-bold">Regional Coverage</h3>
              <p className="text-muted-foreground">
                Exclusive events across Bangladesh, India, Nepal, and Sri Lanka bringing world-class artists to you.
              </p>
            </div>

            <div className="glass-card p-8 space-y-4 hover-glow">
              <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center">
                <Shield className="h-7 w-7 text-primary" />
              </div>
              <h3 className="text-2xl font-bold">Secure Booking</h3>
              <p className="text-muted-foreground">
                Safe and transparent USDT cryptocurrency payments with instant confirmation and full support.
              </p>
            </div>

            <div className="glass-card p-8 space-y-4 hover-glow">
              <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center">
                <Clock className="h-7 w-7 text-primary" />
              </div>
              <h3 className="text-2xl font-bold">Limited Availability</h3>
              <p className="text-muted-foreground">
                Exclusive packages with limited tickets ensure an intimate, uncrowded VIP experience every time.
              </p>
            </div>

            <div className="glass-card p-8 space-y-4 hover-glow">
              <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center">
                <Star className="h-7 w-7 text-primary" />
              </div>
              <h3 className="text-2xl font-bold">5-Star Service</h3>
              <p className="text-muted-foreground">
                Rated 5/5 by thousands of satisfied guests. Dedicated support from booking to event day and beyond.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Upcoming Events */}
      <section className="py-20 md:py-32">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between mb-12">
            <div className="space-y-2">
              <h2 className="text-4xl md:text-5xl font-bold">
                Upcoming <span className="text-gold-gradient">Events</span>
              </h2>
              <p className="text-lg text-muted-foreground">
                Secure your VIP package before tickets sell out
              </p>
            </div>
            <Button
              variant="outline"
              className="hidden md:flex border-primary/50 text-primary hover:bg-primary hover:text-primary-foreground"
              asChild
            >
              <Link href="/events">
                View All Events
                <ChevronRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {upcomingEvents.map((event) => (
              <EventCard key={event.id} event={event} />
            ))}
          </div>

          <div className="mt-12 text-center md:hidden">
            <Button
              variant="outline"
              className="border-primary/50 text-primary hover:bg-primary hover:text-primary-foreground"
              asChild
            >
              <Link href="/events">
                View All Events
                <ChevronRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <TestimonialsSection testimonials={mockTestimonials.slice(0, 6)} />

      {/* CTA Section */}
      <section className="py-20 md:py-32">
        <div className="container mx-auto px-4">
          <div className="glass-card p-12 md:p-16 text-center space-y-8 hover-glow">
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold">
              Ready for Your <span className="text-gold-gradient">VIP Experience</span>?
            </h2>
            <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto">
              Join thousands of music lovers who've elevated their concert experience with Ticket to Luxury.
              Book your exclusive VIP package today.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 pt-4">
              <Button
                size="lg"
                className="bg-gradient-to-r from-primary to-secondary hover:shadow-gold text-lg px-8 py-6"
                asChild
              >
                <Link href="/events">
                  <Ticket className="mr-2 h-5 w-5" />
                  Book Your VIP Package
                </Link>
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="border-primary/50 text-primary hover:bg-primary hover:text-primary-foreground text-lg px-8 py-6"
                asChild
              >
                <Link href="/contact">Contact Our Team</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
